import {
  Component,
  OnInit,
  OnDestroy,
  ViewChild,
  ElementRef,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
} from '@angular/core';

import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ChatbotService, ChatMsg, ChatMode, ChatOpt, ChatPickedPlan, ChatReaction, ChatRoomSummary } from './chatbot.service';
import { ChatRoomComponent } from '../chat-room/chat-room.component';
import { ChatRoomPickerComponent } from '../chat-room-picker/chat-room-picker.component';
import { AdalinePlanCardsComponent } from './plan-cards/adaline-plan-cards.component';
import { AdalineContactCardComponent } from './contact-card/adaline-contact-card.component';
import { Router } from '@angular/router';
import { UsersService } from '../../admin/users/users.service';

@Component({
    selector: 'app-chatbot',
    imports: [ChatRoomComponent, ChatRoomPickerComponent, AdalinePlanCardsComponent, AdalineContactCardComponent],
    changeDetection: ChangeDetectionStrategy.OnPush,
    templateUrl: './chatbot.component.html',
    styleUrls: ['./chatbot.component.scss']
})
export class ChatbotComponent implements OnInit, OnDestroy {

  @ViewChild('msgList')  msgListRef!:  ElementRef<HTMLDivElement>;
  @ViewChild('panel')    panelRef!:    ElementRef<HTMLDivElement>;
  @ViewChild('fabBtn')   fabRef!:      ElementRef<HTMLButtonElement>;

  isOpen      = false;
  messages: ChatMsg[] = [];
  isTyping    = false;
  mode: ChatMode = 'menu';
  unreadCount = 0;
  roomsForPicker: ChatRoomSummary[] = [];
  selectedRoomId: number | null = null;
  /** True while a message with wide content (the plan cards) is on screen —
   * the panel widens on desktop so the three cards fit in one row. */
  wide = false;

  panelLeft   = 16;
  panelBottom = 92;
  fabLeft     = 24;
  fabBottom   = 24;

  private resetTimer: any;
  private dragging      = false;
  private dragTarget: 'panel' | 'fab' | null = null;
  private dragStartX    = 0;
  private dragStartY    = 0;
  private elemStartLeft = 0;
  private elemStartBottom = 0;

  private destroy$ = new Subject<void>();

  private _onMove = this._dragMove.bind(this);
  private _onUp   = this._dragEnd.bind(this);

  constructor(
    private chatbot: ChatbotService,
    private cdr: ChangeDetectorRef,
    private usersService: UsersService,
    private router: Router,
  ) {}

  ngOnInit(): void {
    this.chatbot.messages$.pipe(takeUntil(this.destroy$)).subscribe(msgs => {
      this.messages = msgs;
      this.updateWide();
      this.cdr.markForCheck();
      this.scrollToBottom();
    });

    this.chatbot.isTyping$.pipe(takeUntil(this.destroy$)).subscribe(t => {
      this.isTyping = t;
      this.cdr.markForCheck();
      this.scrollToBottom();
    });

    this.chatbot.mode$.pipe(takeUntil(this.destroy$)).subscribe(m => {
      this.mode = m;
      this.updateWide();
      // Clear unread count only when user enters live chat, not on panel open
      if (m === 'live_chat') {
        this.usersService.setUnreadCount(0);
      }
      this.cdr.markForCheck();
    });

    this.usersService.unreadCount$.pipe(takeUntil(this.destroy$)).subscribe(n => {
      this.unreadCount = n;
      this.cdr.markForCheck();
    });

    this.chatbot.roomsForPicker$.pipe(takeUntil(this.destroy$)).subscribe(rooms => {
      this.roomsForPicker = rooms;
      this.cdr.markForCheck();
    });

    this.chatbot.selectedRoomId$.pipe(takeUntil(this.destroy$)).subscribe(id => {
      this.selectedRoomId = id;
      this.cdr.markForCheck();
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this._removeDragListeners();
  }

  toggle(): void {
    this.isOpen = !this.isOpen;
    if (this.isOpen) {
      clearTimeout(this.resetTimer);
      this.chatbot.init();
      setTimeout(() => this.scrollToBottom(), 50);
    }
    this.cdr.markForCheck();
  }

  close(): void {
    this.isOpen = false;
    this.resetTimer = setTimeout(() => this.chatbot.init(), 280);
    this.cdr.markForCheck();
  }

  onHeaderDragStart(e: MouseEvent | TouchEvent): void {
    this._startDrag(e, 'panel', this.panelLeft, this.panelBottom);
  }

  onFabDragStart(e: MouseEvent | TouchEvent): void {
    this._startDrag(e, 'fab', this.fabLeft, this.fabBottom);
  }

  private _startDrag(e: MouseEvent | TouchEvent, target: 'panel' | 'fab', startLeft: number, startBottom: number): void {
    this.dragging      = false;
    this.dragTarget    = target;
    this.elemStartLeft   = startLeft;
    this.elemStartBottom = startBottom;

    const pt = this._getPoint(e);
    this.dragStartX = pt.x;
    this.dragStartY = pt.y;

    document.addEventListener('mousemove', this._onMove);
    document.addEventListener('touchmove', this._onMove, { passive: false });
    document.addEventListener('mouseup',   this._onUp);
    document.addEventListener('touchend',  this._onUp);
  }

  private _dragMove(e: MouseEvent | TouchEvent): void {
    if (!this.dragTarget) return;
    e.preventDefault();

    const pt = this._getPoint(e);
    const dx =  pt.x - this.dragStartX;
    const dy = -(pt.y - this.dragStartY);

    const vw = window.innerWidth;
    const vh = window.innerHeight;

    if (this.dragTarget === 'panel') {
      const panelW = this.panelRef?.nativeElement?.offsetWidth  ?? 360;
      const panelH = this.panelRef?.nativeElement?.offsetHeight ?? 520;
      this.panelLeft   = Math.min(Math.max(0, this.elemStartLeft   + dx), vw - panelW);
      this.panelBottom = Math.min(Math.max(0, this.elemStartBottom + dy), vh - panelH);
    } else {
      const fabSize = 56;
      this.fabLeft   = Math.min(Math.max(0, this.elemStartLeft   + dx), vw - fabSize);
      this.fabBottom = Math.min(Math.max(0, this.elemStartBottom + dy), vh - fabSize);
    }

    this.dragging = true;
    this.cdr.markForCheck();
  }

  private _dragEnd(): void {
    this.dragTarget = null;
    this._removeDragListeners();
  }

  private _removeDragListeners(): void {
    document.removeEventListener('mousemove', this._onMove);
    document.removeEventListener('touchmove', this._onMove);
    document.removeEventListener('mouseup',   this._onUp);
    document.removeEventListener('touchend',  this._onUp);
  }

  private _getPoint(e: MouseEvent | TouchEvent): { x: number; y: number } {
    if (e instanceof TouchEvent) {
      return { x: e.touches[0]?.clientX ?? 0, y: e.touches[0]?.clientY ?? 0 };
    }
    return { x: e.clientX, y: e.clientY };
  }

  onFabClick(): void {
    if (this.dragging) { this.dragging = false; return; }
    this.toggle();
  }

  onOptionTap(msg: ChatMsg, opt: ChatOpt): void {
    if (!this.isLastInteractiveMsg(msg) || opt.disabled) return;

    const { value, label } = opt;

    if (value.startsWith('_')) {
      this.chatbot.handleFollowUp(value);
      return;
    }

    if (value === 'live_chat') {
      this.chatbot.handleMenuChoice(value);
      return;
    }

    switch (this.mode) {
      case 'menu':
        this.chatbot.handleMenuChoice(value);
        break;
      case 'publish_category':
        this.chatbot.selectPublishCategory(value, label, () => this.close());
        break;
      case 'navigate':
        this.chatbot.navigateTo(label, value, () => this.close());
        break;
      case 'qa':
        this.chatbot.answerQA(value, label);
        break;
    }
  }

  readonly REACTIONS = ['👍', '👎', '❤️', '😂'];

  toggleReaction(msg: ChatMsg, emoji: string): void {
    if (!msg.reactions) { msg.reactions = []; }
    const existing = msg.reactions.find(r => r.emoji === emoji);
    if (existing) {
      if (existing.reacted) {
        existing.count--;
        existing.reacted = false;
        if (existing.count === 0) {
          msg.reactions = msg.reactions.filter(r => r.emoji !== emoji);
        }
      } else {
        existing.count++;
        existing.reacted = true;
      }
    } else {
      msg.reactions.push({ emoji, count: 1, reacted: true });
    }
    this.cdr.markForCheck();
  }

  backToMenu(): void { this.chatbot.backToMenu(); }

  /** "שלח לנו הודעה" in the contact card -> the /contact page form. */
  onOpenContactForm(): void {
    this.router.navigate(['/contact']);
    this.close();
  }

  onPlanSelected(plan: ChatPickedPlan): void {
    this.chatbot.selectPlan(plan, () => this.close());
  }

  /** Same max width as .cb-panel--wide in the SCSS. */
  private static readonly WIDE_PANEL_PX = 760;

  private updateWide(): void {
    const wide = this.mode !== 'live_chat' && this.mode !== 'room_picker'
      && this.messages.some(m => m.widget === 'ad_plans' || m.widget === 'subscription_plans');
    if (wide === this.wide) return;
    this.wide = wide;
    if (wide) {
      // The panel is positioned by its LEFT edge (drag). Pull it back so the
      // wider panel never runs off the right side of the viewport.
      const width = Math.min(ChatbotComponent.WIDE_PANEL_PX, window.innerWidth - 32);
      this.panelLeft = Math.max(0, Math.min(this.panelLeft, window.innerWidth - width - 16));
    }
  }

  onRoomPicked(room: ChatRoomSummary): void {
    this.chatbot.selectRoom(room);
  }

  onRoomDeleted(room: ChatRoomSummary): void {
    this.chatbot.deleteRoom(room);
  }

  /** The room open in live_chat mode got deleted from within itself — go
   * back to the main menu rather than leaving an empty chat view up. */
  onChatDeleted(): void {
    this.chatbot.backToMenu();
  }

  trackById(_: number, msg: ChatMsg): string { return msg.id; }

  isLastInteractiveMsg(msg: ChatMsg): boolean {
    const interactive = this.messages.filter(m => m.options?.length);
    return interactive[interactive.length - 1]?.id === msg.id;
  }

  lines(text: string): string[] { return text.split('\n'); }

  private scrollToBottom(): void {
    requestAnimationFrame(() => {
      const el = this.msgListRef?.nativeElement;
      if (el) el.scrollTop = el.scrollHeight;
    });
  }
}
